import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowUpI from '@mui/icons-material/ArrowDropUp'
import ArrowDownI from '@mui/icons-material/ArrowDropDown'
import { Divider, Grid } from '@mui/material'
import { getFormattedDate } from 'lib/helpers/shared.helpers'
import {
  accountingStepFourAccountingDocumentation,
  formatDate,
  formatName,
} from 'lib/utils/Accounting/accounting.utils'
import { useAccountingContext } from 'containers/Accounting/AccountingContainer/AccountingContainer'
import Button from 'components/Shared/Button/Button'
import { TaskAlt } from 'components/Shared/CustomIcons'
import FileCard, { FileCardAlt } from 'components/Shared/FileCard/FileCard'
import SydDialog from 'components/Shared/SydDialog/SydDialog'
import FileUploader from 'components/FormComponents/FileUploader/FileUploader'
import Form from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import AccountingFileDropzoneContentMultiple from '../AccountingFileDropzoneContentMultiple/AccountingFileDropzoneContentMultiple'
import useAccountingStepFour, {
  IAccountingStepFourState,
} from './useAccountingStepFour'
import './styles.scss'

const AccountingStepFour = () => {
  const {
    stepChangeLoading,
    checkForExistingFileNameInAccountingProcess,
    handleFileDownload,
    handleFileRetryUpload,
    handleStepBack,
  } = useAccountingContext()

  const {
    comment,
    clientCommentsDialog,
    continueButtonProps,
    fileStatuses,
    filesToValidate,
    limitFileView,
    selectedFile,
    totalFiles,
    uploadedAndValidatedFiles,
    uploadedFiles,
    uploadedPreviousStepsRequiredFiles,
    uploadingFile,
    validCommentsDialog,
    validRenameDialog,
    fileUploadHandler,
    handleCommentsFormSubmit,
    handleRenameFormSubmit,
    handleSkipSubmit,
    updateStepFourState,
  } = useAccountingStepFour()

  return (
    <div className="step-container step-four">
      <div className="content">
        <p className="step-description">
          {(!totalFiles.length || uploadedFiles.length > 0) && (
            <>Espera a que el contador suba la "Documentación Contable".</>
          )}
          {filesToValidate.length > 0 && (
            <>Valida la “Documentación Contable” cargada por el contador.</>
          )}
        </p>

        <Grid container direction="row">
          {!!comment && (
            <Grid item xs={12} className="step-comments">
              <div className="title-comments">
                <p>Comentarios cliente:</p>
              </div>
              <div className="comments">
                <p>{comment}</p>
              </div>
            </Grid>
          )}
        </Grid>

        <Grid container direction="row" spacing={2}>
          {!totalFiles.length && (
            <Grid item xs={12} className="uploaded-documentation-list">
              <div className="initial-dropzone">
                <FileUploader
                  className="drop-zone"
                  color="secondary"
                  label="Subir archivos"
                  onValue={fileUploadHandler(totalFiles)}
                  type={accountingStepFourAccountingDocumentation?.type}
                />
                {!!uploadingFile && (
                  <FileCardAlt
                    title={formatName(uploadingFile)}
                    subtitle={formatDate(new Date().toISOString())}
                    type={'File'}
                    actions={[
                      {
                        label: 'Reintentar',
                        action: () => handleFileRetryUpload(uploadingFile),
                      },
                    ]}
                    error={uploadingFile.error}
                    loading={uploadingFile.activity}
                    className="file-zone"
                  />
                )}
              </div>
            </Grid>
          )}

          {totalFiles.length > 0 && (
            <>
              <Grid item xs={4} className="uploaded-documentation-list">
                <div className="initial-dropzone">
                  <FileUploader
                    className="drop-zone"
                    color="secondary"
                    label="Subir archivos"
                    onValue={fileUploadHandler(totalFiles)}
                    type={accountingStepFourAccountingDocumentation?.type}
                  />
                  {!!uploadingFile && (
                    <FileCardAlt
                      title={formatName(uploadingFile)}
                      subtitle={formatDate(new Date().toISOString())}
                      type={'File'}
                      actions={[
                        {
                          label: 'Reintentar',
                          action: () => handleFileRetryUpload(uploadingFile),
                        },
                      ]}
                      error={uploadingFile.error}
                      loading={uploadingFile.activity}
                      className="file-zone"
                    />
                  )}
                </div>
              </Grid>

              {totalFiles.length > 0 &&
                totalFiles.map(({ idWsfile }) => (
                  <Grid
                    item
                    xs={4}
                    key={`accounting-file-dropzone-content-${idWsfile}`}
                    className="uploaded-documentation-list">
                    <AccountingFileDropzoneContentMultiple<IAccountingStepFourState>
                      fileStatuses={fileStatuses}
                      fileStatusesKey={'fileStatuses'}
                      filesToValidate={filesToValidate}
                      openCommentsDialogKey="fileStatuses"
                      uploadedAndValidatedFiles={uploadedAndValidatedFiles}
                      uploadedFiles={uploadedFiles}
                      uploadItemId={idWsfile}
                      updateState={updateStepFourState}
                    />
                  </Grid>
                ))}
            </>
          )}

          <Grid item xs={12}>
            <p className="step-indicator">Documentación contable (Contador)</p>
            <div className="step-buttons-actions">
              <Button
                color="secondary"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleStepBack}
                activity={stepChangeLoading}>
                Ir a la sección anterior
              </Button>

              <div className="buttons-actions-right">
                {!totalFiles.length && (
                  <Button
                    id="skipDocumentationBtn"
                    variant="outlined"
                    color="warning"
                    onClick={() => handleSkipSubmit()}
                    activity={stepChangeLoading}>
                    Omitir Documentación Contable
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  {...continueButtonProps}
                  activity={stepChangeLoading}>
                  {continueButtonProps.text}
                </Button>
              </div>
            </div>
          </Grid>
        </Grid>

        <Grid container direction="row">
          <Grid item xs={12}>
            <Divider className="step-divider" />
          </Grid>
        </Grid>

        <Grid container direction="row">
          <Grid item xs={12}>
            <Grid container spacing={2} className="documentation-list">
              <Grid item xs={12}>
                <div className="documentation-title-container">
                  <TaskAlt color="secondary" fontSize="large" />
                  <h4>Documentos cargados</h4>
                </div>
              </Grid>
              {uploadedPreviousStepsRequiredFiles.map((f, i) =>
                limitFileView && i > 3 ? (
                  <></>
                ) : (
                  <Grid item xs={3} key={i}>
                    <FileCard
                      title={formatName(f)}
                      subtitle={getFormattedDate(f.createdAt)}
                      type={'File'}
                      actions={[
                        {
                          label: 'Descargar archivo',
                          action: () => handleFileDownload(f),
                        },
                      ]}
                    />
                  </Grid>
                )
              )}
              {uploadedPreviousStepsRequiredFiles.length > 4 && (
                <Grid item xs={12} className="dropdown-action">
                  <Button
                    id="seeAllBtn"
                    color="secondary"
                    endIcon={limitFileView ? <ArrowDownI /> : <ArrowUpI />}
                    onClick={() =>
                      updateStepFourState(draft => {
                        draft.limitFileView = !draft.limitFileView
                      })
                    }>
                    {limitFileView ? 'Ver todos' : 'Ver menos'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>
      <SydDialog
        open={clientCommentsDialog}
        title="Agregar comentarios"
        subtitle="Antes de terminar la revisión puedes agregar comentarios para ayudar al cliente a completar la documentación."
        primaryButtonText="Agregar"
        disablePrimaryButton={!validCommentsDialog}
        secondaryButtonText="Cancelar"
        onCancel={() => {
          updateStepFourState(draft => {
            draft.clientCommentsDialog = false
            draft.validCommentsDialog = false
          })
        }}
        form="commentsForm"
        fullWidth
        sideButtons>
        <Form
          id="commentsForm"
          onSubmit={handleCommentsFormSubmit}
          onValidity={(v: boolean) =>
            updateStepFourState(draft => {
              draft.validCommentsDialog = v
            })
          }
          reset>
          <TextField
            variant="outlined"
            name="comment"
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={5}
            maxRows={5}
            withCounter
            fullWidth
            required
          />
        </Form>
      </SydDialog>

      <SydDialog
        open={!!selectedFile}
        size="sm"
        title="Renombrar un archivo"
        primaryButtonText="Renombrar"
        disablePrimaryButton={!validRenameDialog}
        secondaryButtonText="Cancelar"
        onCancel={() => {
          updateStepFourState(draft => {
            draft.selectedFile = undefined
            draft.validRenameDialog = false
          })
        }}
        form="renameFileForm"
        sideButtons>
        <Form
          id="renameFileForm"
          onSubmit={handleRenameFormSubmit}
          onValidity={(v: boolean) =>
            updateStepFourState(draft => {
              draft.validRenameDialog = v
            })
          }
          reset>
          <TextField
            name="name"
            label="Nombre del archivo"
            value={selectedFile?.name}
            validate={(v: string) =>
              checkForExistingFileNameInAccountingProcess(v)
            }
            fullWidth
            required
          />
        </Form>
      </SydDialog>
    </div>
  )
}

export default AccountingStepFour
